/**
 * Mermaid chart sources for the "How migration works" reference block on
 * /sa/sstv2-migration. Extracted from `Sstv2MigrationFlowDoc.tsx` so the
 * component stays presentational (and under the ~400-line component budget)
 * while these diagrams — which are the part that drifts — sit in one file that
 * can be diffed against the backend on its own.
 *
 * REWRITTEN 2026-07-29 (DEV-193). The previous revision described a pipeline that
 * no longer exists: a second SQS queue, chunk messages of ≤300 id mappings,
 * `SendMessageBatch` fan-out, `BatchSize 1 · MaximumConcurrency 5`, and
 * "materialize migrated / adopted v2 Reservations". All of it was deleted with the
 * cutover-atomic redesign. The two facts that replaced it:
 *
 *  1. **ONE queue.** Ingestion is a pure ACCUMULATOR (D15) — it transcribes the
 *     partner payload into immutable mapping items and does nothing else. No
 *     lookup, no partner read, no reservation write.
 *  2. **ONE durable execution per attempt** (D13), started at cutover by the SQS
 *     consumer with `DurableExecutionName: flip-{migrationId}`. The reservation
 *     row is updated IN PLACE — migration never creates a second Reservation
 *     Thing (D2), so "materialized N" is the wrong verb. It is "flipped N".
 *
 * Ground truth these mirror (verify before editing):
 *  - `apps/sl2/template.yml` — the ONE ingestion queue `Sl2Sstv2SqsQueue` →
 *    `LambdaSstv2SqsConsumer` (BatchSize 5, ReportBatchItemFailures) and
 *    `LambdaSstv2MigrationFlip` (`DurableConfig`, `ExecutionTimeout: 86400`,
 *    and deliberately NO `Events:` block — never behind an ESM).
 *  - `src/sl2-partners/src/sstv2/sstv2EventTypes.ts` — the 23 event literals.
 *  - `src/sl2-partners/src/sstv2/sstv2SqsConsumer.ts` — dedupe, envelope gate,
 *    dispatch registries.
 *  - `src/things/sstv2MigrationMapping/sstv2MigrationMappingRepo.ts` — the
 *    `BatchWriteItem` write-down and the flip's `ConsistentRead` page query.
 *  - `src/sl2-partners/src/sstv2/helpers/sstv2MigrationFlipStart.ts` — the
 *    `InvokeCommand(Event)` start, the liveness probe, the explicit stop.
 *  - `src/fnSstv2MigrationFlip.ts` — guard / wait / page loop / fold, and the
 *    hand-written wait strategy.
 *  - `src/sl2-partners/src/sstv2/migrationProcessor/sstv2MigrationReconciler.ts`
 *    + `sstv2MigrationStalledFlip.ts` — the tick's three jobs.
 *
 * Unicode (≤, →, ·) is deliberate: mermaid renders labels as HTML, so a literal
 * `<=` or a `|` inside a label can break the parse. A `;` inside a label is worse
 * still — it ENDS THE STATEMENT, and the parse error is reported against the NEXT
 * line. There are zero semicolons inside labels in this file for that reason, and
 * `sstv2FlowCharts.test.ts` asserts it.
 *
 * REVISED 2026-07-31 (DEV-193) — **D37 supersedes D5**. Until `d9047759b` these
 * diagrams stated, in four separate places, that reservation history is never
 * flipped and that `historyProcessed` is credited at ingestion. Both are now false:
 *
 *  3. **BOTH streams are flipped.** `FLIP_STREAMS = ["active", "history"]` in
 *     `src/fnSstv2MigrationFlip.ts`. A historical reservation differs from an active
 *     one only by its `status` — same page loop, same bulk read, same conditional
 *     write, same `preV2` undo record, same rollback. There is no second code path.
 *  4. **The streams are walked SEQUENTIALLY (active first) and folded
 *     INDEPENDENTLY.** Each has its own arrival gate, its own `walkedEveryPage` and
 *     its own absolute numerator SET, because `allActiveBatchesReceived` and
 *     `allHistoryBatchesReceived` are set by different events in no fixed order.
 *     ⚠ The diagrams must NOT show one page carrying both kinds: `kind` is in the
 *     sort key (D11) precisely so each partition is queried without a post-filter.
 *  5. **`historyProcessed` is no longer auto-credited at ingestion** (§4.4 trap 2).
 *     Under D5 the mapping write WAS the processing. Leaving that credit in place
 *     would fire `COMPLETE` before the history walk had run at all.
 *
 * Scale facts worth keeping on the diagrams: `MAPPING_PAGE_SIZE = 250` and
 * `MAX_PAGES = 200` PER STREAM. A real 2026-07-27 run recorded ~3042 history
 * mappings for one locker against 3 active — 13 pages, comfortably inside the cap.
 */

/**
 * Static topology: what the plumbing IS. The three facts this diagram exists to
 * make unmissable —
 *  (1) there is exactly ONE queue, carrying both live reservation traffic and
 *      migration events. The isolation a second queue used to buy is now
 *      structural: ingestion does one batched write and no reads, so a
 *      30k-reservation migration storm cannot occupy the consumer;
 *  (2) the flip is a DURABLE EXECUTION, not a queue consumer. The consumer
 *      STARTS it and must never HOST it — `withDurableExecution` on the consumer,
 *      or an ESM on the flip function, silently caps the whole flip at 15 minutes
 *      with no error;
 *  (3) the 15-minute reconcile tick STAYS, with three jobs, each blind to a
 *      failure the other two cover.
 */
export const TOPOLOGY_CHART = `flowchart TB
    classDef queue fill:#e3f2fd,stroke:#1565c0,stroke-width:2px,color:#0d3c61
    classDef dlq fill:#fdecea,stroke:#c62828,color:#7f1d1d
    classDef lambda fill:#ede7f6,stroke:#5e35b1,stroke-width:2px,color:#311b92
    classDef durable fill:#e8eaf6,stroke:#283593,stroke-width:3px,color:#1a237e
    classDef store fill:#eceff1,stroke:#455a64,color:#263238
    classDef live fill:#e8f5e9,stroke:#2e7d32,color:#1b5e20
    classDef mig fill:#fff3e0,stroke:#ef6c00,color:#e65100
    classDef ext fill:#f5f5f5,stroke:#757575,color:#212121
    classDef gate fill:#fffde7,stroke:#f9a825,color:#4e342e

    SST["SST v2 (SirSteward)<br/>publishes as a dedicated IAM user — sqs:SendMessage only"]:::ext
    EB["EventBridge schedule<br/>rate(15 minutes)"]:::ext

    QI[("THE ONLY QUEUE · INGESTION<br/>sl2-sstv2-sqs-queue<br/>SSE-SQS · visibility 180s · maxReceive 3")]:::queue
    DLQI[("sl2-sstv2-sqs-dlq · 14d retention · depth alarm<br/>⚠ a dead envelope here means mapping items that were NEVER WRITTEN<br/>re-driven by the tick, capped at 3 attempts (MblsRedriveCount)")]:::dlq
    CI["fnSstv2SqsConsumer — a PLAIN Lambda<br/>ESM · BatchSize 5 · ReportBatchItemFailures<br/>writes a trace record per message"]:::lambda
    GATE{"per record · dedupe hash (48h TTL, marked only on success)<br/>then strict envelope + payload validation"}:::gate
    DROP["DISCARDED, never retried<br/>malformed JSON · bad envelope · unknown V1 locker<br/>metric Sstv2MigrationInvalidEnvelope + alarm"]:::dlq

    SST -->|"all 23 event types share ONE entry point"| QI
    QI ==>|"event source mapping"| CI
    QI -.->|"3 failed receives"| DLQI
    CI --> GATE
    GATE -->|"invalid / not retryable"| DROP
    GATE -.->|"retryable → reported as a batch item failure, redelivered"| QI

    subgraph LIVE["LIVE RESERVATION + LOCKER OPS · 14 event types · nothing to do with migration"]
      direction TB
      NEV["reservation:created · updated · delivered · pickedUp<br/>cancelled · expired · deleted · adminAccessed · paymentProcessed<br/>locker:created · updated · deleted · door:updated · door:status"]:::live
      NW["update the EXISTING Reservation Thing<br/>sstReservation ← V1-mapped payload, sstReservationVersion = v2<br/>+ ReservationAud row + MEvent audit"]:::live
    end

    subgraph MIG["MIGRATION OPS · 9 migration:* event types · INGESTION IS A PURE ACCUMULATOR (D15)"]
      direction TB
      MEVR["migration:reservations:active:done<br/>migration:reservations:inactive:done<br/>carry success_list + failure_list"]:::mig
      MACC["BatchWriteItem IMMUTABLE mapping items, 25 per call<br/>pure payload transform — NO gsi1 read · NO partner read · NO reservation write<br/>kind is IN the sk (D11), so each stream is queried without a post-filter<br/>⚠ received counters ADD only — history is NOT credited as processed here (D37)<br/>+ Sstv2MigrationFailure rows from failure_list"]:::mig
      MEVL["migration:locker:migrated<br/>reservations:history:completed (alias locker:completed)<br/>rollback:started · rollback:done"]:::mig
      MFOLD["fold the ONE Sstv2Migration job record<br/>key = tenantId + sl2_locker_id (the attempt id)<br/>provision / activate / supersede / delete Locker Things"]:::mig
      MCUT["migration:locker:operational — CUTOVER<br/>activate V2 · supersede V1 · drain ActionReqs · fold OPERATIONAL"]:::mig
      MDROP["migration:rollback:reservations:done<br/>recognized then deliberately dropped — the unwind is rollback:done"]:::mig
    end

    GATE -->|"non-migration event"| NEV
    NEV --> NW
    GATE -->|"migration:* reservation batches"| MEVR
    MEVR --> MACC
    GATE -->|"migration:* locker + rollback"| MEVL
    MEVL --> MFOLD
    GATE -->|"migration:locker:operational"| MCUT
    GATE --> MDROP

    DF["fnSstv2MigrationFlip — ONE DURABLE EXECUTION PER ATTEMPT<br/>DurableExecutionName flip-{migrationId} IS the dedup primitive<br/>ExecutionTimeout 24h · Timeout 900s is PER INVOCATION<br/>⚠ NO Events block, EVER — an ESM caps the whole flip at 15 min silently"]:::durable
    DFW["guard → then FOR EACH STREAM in order, ACTIVE then HISTORY (D37)<br/>waitForCondition(that stream ARRIVED, HAND-WRITTEN deadline)<br/>→ page loop: ONE step per page, query AND flip together, 250 per page<br/>→ fold THAT stream independently. MAX_PAGES 200 per stream<br/>Steps return only count + lastEvaluatedKey (256 KB cap)"]:::durable
    FLIP["per mapping of EITHER kind, ONE conditional write on the row's OWN (pk, sk)<br/>flips 10 identity fields IN PLACE + writes the preV2 undo record<br/>id · createdAt · every customer field UNTOUCHED · no second Thing<br/>the mapper MERGES onto the existing payload, so V1-only fields survive<br/>no v1 ancestor ⇒ ADOPT a new Thing on a deterministic id"]:::mig

    MCUT ==>|"InvokeCommand · InvocationType Event · async is MANDATORY"| DF
    DF --> DFW
    DFW --> FLIP

    subgraph TICK["15-MIN RECONCILE TICK · on fnSstv2MigrationProcessor · THREE jobs, none subsumes another"]
      direction TB
      J1["Job 1 — re-drive the INGESTION DLQ (repointed off the deleted queue 2)<br/>covers: an ingest batch died ⇒ its mappings were NEVER WRITTEN<br/>safe to replay (envelope dedupe + immutable items) · capped at 3 attempts"]:::mig
      J2["Job 2 — reconcile our counters against getMigrationStatus<br/>covers: we and the partner disagree · ADVISORY, writes nothing<br/>the only SYMPTOM job 1's failure case has"]:::mig
      J3["Job 3 — stalled-flip detection<br/>cut over + unflipped mappings + NO live execution ⇒ start one<br/>covers: the execution FAILED terminally (no automatic retry exists)<br/>BLIND to mappings that were never written — that is job 1's half"]:::mig
    end

    EB ==>|"reconcile tick · its own invocation, unrelated to the flip"| TICK
    J1 -.->|"re-send verbatim"| QI
    DLQI -.->|"≤500 messages per run"| J1
    J3 -.->|"ListDurableExecutionsByFunction — is a run alive?"| DF

    DDB[("SL2 DynamoDB<br/>AppTable0 (Things, Sstv2Migration, mapping items, failures) + IdempotencyTable")]:::store
    IOT["AWS IoT — publishSstv2IoTEvent, V2-raw payload"]:::ext
    COMMS["comms API (HTTPS) + direct Lambda invoke<br/>only on delivered · expired · paymentProcessed"]:::ext
    SSTAPI["SST v2 REST<br/>getReservationsByIds · getMigrationStatus"]:::ext

    NW --> DDB
    NW --> IOT
    NW --> COMMS
    MACC --> DDB
    MFOLD --> DDB
    MCUT --> DDB
    FLIP --> DDB
    DFW -->|"ONE bulk read for the WHOLE page — the old id cap was an SQS message-size limit, not a partner one"| SSTAPI
    J2 -.-> SSTAPI`

// Forward migration: the 3 ordered partner steps → ONE queue → the accumulator →
// cutover → ONE durable execution walking TWO streams.
// Facts the diagram must keep straight: (1) every event of a run carries the
// attempt's `sl2_locker_id` — that id is the migration/job id we key on;
// (2) the V2 locker Thing is born on the FIRST event of the attempt, NOT at
// cutover; (3) ingestion writes mapping items and NOTHING else — the reservation
// row stays fully V1 until the flip; (4) BOTH kinds are flipped (D37) — active
// first, then history, each behind its own arrival gate and its own fold, and
// ingestion credits neither stream as processed.
export const FORWARD_CHART = `sequenceDiagram
    autonumber
    actor Ops as Operator / SST tooling
    participant SST as SST v2 (SirSteward)
    participant QI as Q1 - the only queue
    participant CI as fnSstv2SqsConsumer
    participant DF as durable execution (flip)
    participant DDB as SL2 DynamoDB

    rect rgb(227,242,253)
    Note over Ops,DDB: Step 1 - create_locker (migrate ACTIVE reservations)
    Ops->>SST: POST /mobilus/migration/create_locker
    SST-->>Ops: { client_uuid }
    SST-)QI: migration:locker:migrated
    QI-)CI: migrated (BatchSize 5)
    CI->>DDB: dedupe hash check (IdempotencyTable, 48h TTL)
    CI->>DDB: open Sstv2Migration job (key = sl2_locker_id) -> ACTIVE_MIGRATED
    CI->>DDB: provisionV2Locker - V2 Locker Thing born here (pendingActivation)
    SST-)QI: migration:reservations:active:done (success_list + failure_list)
    QI-)CI: active:done
    CI->>DDB: BatchWriteItem IMMUTABLE mapping items, 25 per call (kind = active)
    Note over CI,DDB: ACCUMULATOR ONLY - no gsi1 read, no partner read, no reservation write
    CI->>DDB: counters ADD + allActiveBatchesReceived token
    CI->>DDB: Sstv2MigrationFailure rows from the partner failure_list
    Note over CI,DDB: the reservation row is STILL FULLY V1 - the V1 locker is still live
    end

    rect rgb(232,245,233)
    Note over Ops,DDB: Step 2 - history_migration (reservation HISTORY, batched)
    Ops->>SST: POST /mobilus/migration/history_migration/{uuid}
    SST-)QI: migration:reservations:inactive:done (one per batch)
    QI-)CI: inactive:done
    CI->>DDB: BatchWriteItem mapping items (kind = history) - each carries sstV2ResId
    Note over CI,DDB: D37 (2026-07-31, superseding D5): these ARE flipped later, exactly like active.<br/>⚠ historyProcessed is NOT credited here any more - the write is not the work.<br/>Crediting it at ingestion fires COMPLETE before the history walk has run.
    SST-)QI: migration:reservations:history:completed
    QI-)CI: history:completed
    CI->>DDB: allHistoryBatchesReceived flag + fold phase -> COMPLETED
    Note over CI,DDB: partner COMPLETED means the PARTNER finished SENDING.<br/>It does not mean we finished writing - that is processingState COMPLETE
    end

    rect rgb(255,244,224)
    Note over Ops,DDB: Step 3 - local_migration (CUTOVER, HTTP often 504)
    Ops->>SST: POST /mobilus/migration/local_migration/{uuid}
    SST-)QI: migration:locker:operational
    QI-)CI: operational
    CI->>DDB: provisionV2Locker (idempotent) + activate V2 locker
    CI->>DDB: supersede V1 locker (migrationDisabled + supersededBy)
    CI->>DDB: drain in-flight ActionReq Things on the V1 locker
    CI->>DDB: fold phase -> OPERATIONAL
    CI->>DF: InvokeCommand, InvocationType Event, DurableExecutionName flip-{migrationId}
    Note over CI,DF: duplicate start raises DurableExecutionAlreadyStartedException - treat as success
    Note over CI: the handler writes NO reservation and does NOT wait
    end

    rect rgb(237,231,246)
    Note over DF,DDB: ONE execution - checkpointed steps - spans invocations - 24h budget
    DF->>DDB: step guard - attempt not rolled back

    loop FOR EACH STREAM in order - ACTIVE first, then HISTORY (D37)
    DF->>DDB: waitForCondition - THAT stream ARRIVED, EXPLICIT deadline<br/>⚠ ARRIVED, never COMPLETE - the flip is the only writer of processed,<br/>so waiting on completion deadlocks on the work it blocks<br/>on expiry write a failure + terminal phase, never a bare throw
    loop one step per page - query AND flip together, cursor only, 250 per page
    DF->>DDB: query THIS stream's mapping partition, ConsistentRead<br/>kind is in the sk (D11), so no page ever mixes the two kinds
    DF->>SST: ONE getReservationsByIds for the whole page<br/>terminal reservations ARE served - verified live 2026-07-31
    DF->>DDB: replay-safe order: migratedToV2MigrationId already this attempt -> SKIP
    DF->>DDB: else gsi1 resolve -> conditional flip IN PLACE + preV2 undo record
    Note over DF,DDB: the mapper MERGES onto the row's existing payload, so a closed<br/>reservation keeps the 18 V1-only fields the partner returns as ""
    Note over DF,DDB: gsi1 MISS -> re-read once, THEN adopt - a bare miss duplicates the Thing
    Note over DF,DDB: per-mapping error -> Sstv2MigrationFailure row, never a throw
    end
    DF->>DDB: step fold THIS stream - own gate, own walkedEveryPage, own numerator SET
    end

    DF->>DDB: job COMPLETE
    Note over DF,DDB: OLAP v4 needs no manual recompute - the dirty-slice trigger queues<br/>the old AND new locker slices of every flipped row (LOCKER-501)
    Note over DF,DDB: the two streams finish INDEPENDENTLY - active typically at cutover,<br/>history possibly long after. A stalled flip is restarted by the 15-min tick
    end`

// Rollback: re-activate V1, revert the flipped rows' IDENTITY in place, archive
// only the adopted ones, fold to ROLLED_BACK. The job record itself survives as
// the audit trail of the failed attempt; the next attempt gets its own record
// under its own `sl2_locker_id`.
//
// Since D37 the unwind covers BOTH streams — `for (const kind of FLIP_STREAMS)`
// runs in `mode: "rollback"` exactly as it does in `mode: "flip"`. A rollback is
// therefore the same scale problem as the forward walk, but cheaper: every value
// comes from the row's own `preV2` record, so there is no partner call at all.
//
// The distinction this diagram exists to make: RESTORED is not DELETED. A flipped
// row is reverted and STAYS LIVE — only adopted / old-model rows are archived.
// Conflating the two reads as data loss.
export const ROLLBACK_CHART = `sequenceDiagram
    autonumber
    actor Ops as Operator / SST tooling
    participant SST as SST v2
    participant QI as Q1 - the only queue
    participant CI as fnSstv2SqsConsumer
    participant DF as durable execution (mode = rollback)
    participant DDB as SL2 DynamoDB

    Note over SST,DDB: Between cutover and rollback the parcel may have been PICKED UP on V2,<br/>so the row can be DELIVERED with a real delivery_time

    Ops->>SST: POST /mobilus/migration/rollback/{uuid}
    SST-)QI: migration:rollback:started
    QI-)CI: rollback:started
    CI->>DDB: fold phase -> ROLLBACK_STARTED
    CI->>DF: StopDurableExecution - cancel a running flip explicitly, not cooperatively

    SST-)QI: migration:rollback:reservations:done
    QI-)CI: rollback:reservations:done
    Note over CI: recognized, then DROPPED - the unwind happens on rollback:done

    SST-)QI: migration:rollback:done
    QI-)CI: rollback:done
    CI->>DDB: re-activate V1 locker (migrationDisabled = false, supersededBy = null)
    CI->>DF: InvokeCommand - same function, mode = rollback

    rect rgb(255,235,238)
    Note over DF,DDB: the SAME two mapping partitions, opposite direction - revert IDENTITY ONLY<br/>since D37 history is unwound exactly like active, in the same stream order
    DF->>DDB: RESTORE each flipped row IN PLACE - it stays LIVE, nothing is removed
    Note over DF,DDB: every value comes from row.preV2, the undo record the flip wrote
    DF->>DDB: gsiSstRespk, gsi1sk, sstReservation._id ← preV2.sstResId
    DF->>DDB: doorId ← preV2.doorId (a Thing id), door._id ← preV2.sstDoorId
    DF->>DDB: lockerId, gsi2pk ← preV2.lockerId, door.locker_id ← preV2.sstLockerId
    DF->>DDB: sstReservationVersion ← v1
    Note over DF,DDB: migratedToV2MigrationId LEFT IN PLACE (D7)<br/>status, delivery_time, payment, the 18 V1-only fields ALL KEPT
    Note over DF,DDB: NO SST call, NO snapshot restore - the pickup is not un-done
    DF->>DDB: ARCHIVE only ADOPTED rows (no v1 state to revert) + old-model rows<br/>distinct non-allow-listed thingTypeMrn, so OLAP sees only the REMOVE
    DF->>DDB: step fold -> ROLLED_BACK (counters kept - this job is a closed record)
    end

    Note over DF,DDB: reservationsRestored and reservationsDeleted are DIFFERENT outcomes.<br/>Restored rows are live on V1 — only archived rows left the live table.
    Note over DF,DDB: A late ingest for a rolled-back attempt re-puts identical mapping bytes<br/>and the flip's phase re-check drops it - nothing can corrupt the unwind`

/**
 * The three recovery paths, and the blindness that makes all three necessary.
 *
 * This is the diagram the prose could never carry: the arrows that matter are the
 * *negative* ones — job 3 cannot see a mapping that was never written, and job 1
 * cannot see an execution that died. Losing either job silently converts a
 * recoverable partial migration into a permanent one whose only symptom is a
 * mismatch in an advisory metric.
 *
 * ⚠ D37 widened what job 3 has to cover. The flip is now TWO sequential walks, so an
 * attempt can be stalled on the HISTORY stream hours after the active stream
 * finished and the locker has been serving happily on V2 the whole time. "Cut over
 * and working" is no longer evidence that the flip is done.
 *
 * ⚠ EXTENSION POINT. `recovery-reachability` (in flight, `apps/sl2/src/**`, NOT
 * merged at `0cd056dbf`) adds a per-stream fold provenance stamp and a set of
 * completion outcomes that change job 3's answer from "do the counters balance" to
 * "who certified them". This diagram deliberately does NOT enumerate those outcomes
 * — it describes the detector's SHAPE, which that work does not change. When it
 * lands, the new vocabulary belongs in `./sstv2Vocabulary` (a data change) and this
 * chart needs at most one node edited, not a rewrite.
 */
export const RECOVERY_CHART = `flowchart TD
    T["15-min reconcile tick<br/>on fnSstv2MigrationProcessor"] --> J1
    T --> J2
    T --> J3

    J1["Job 1 — re-drive the INGESTION DLQ<br/>repointed off the deleted queue 2's DLQ"]
    J2["Job 2 — reconcile our counters against<br/>the partner's getMigrationStatus"]
    J3["Job 3 — stalled-flip detection"]

    J1 --> F1["Failure it covers:<br/>an ingest batch died, so its mapping items<br/>were NEVER WRITTEN — there is no work set"]
    F1 --> R1["Replay is safe: envelope dedupe in the<br/>IdempotencyTable + IMMUTABLE mapping items<br/>⇒ a re-put writes identical bytes"]
    R1 --> C1["Capped at 3 attempts per message (MblsRedriveCount),<br/>else a poison envelope bounces every 15 min forever.<br/>Exhausted messages stay in the DLQ for the viewer"]

    J2 --> F2["Failure it covers:<br/>we and the partner disagree on the count.<br/>The only SYMPTOM job 1's failure case has"]
    F2 --> C2["ADVISORY only — logs and a metric.<br/>Writes nothing, so it detects but cannot fix"]

    J3 --> F3["Failure it covers:<br/>the durable execution FAILED TERMINALLY.<br/>No automatic retry exists after that"]
    F3 --> R3["Cut over + mappings exist + rows unflipped + no live<br/>execution ⇒ start a fresh one. Restart needs no state<br/>unwound: that is DurableExecutionName dedup over a latch"]
    R3 --> C3["Answerable because durable executions are QUERYABLE.<br/>A self-invoke chain leaves no registry, and a chain that<br/>dies mid-way leaves no record it ever ran"]

    F3 --> D37["⚠ since D37 the flip is TWO sequential walks (active, then history)<br/>so an attempt can stall on HISTORY long after the locker went live on V2.<br/>A serving V2 locker is NOT evidence that the flip finished"]

    F1 -.->|"INVISIBLE to"| J3
    F3 -.->|"INVISIBLE to"| J1`
